/* tslint:disable */
/* eslint-disable */
/**
 * Business Orchestration Layer (BOL) API
 * Business Orchestration layer (BOL) API. Public part, exposed to BOL client applications.  Glossary | Definition -- | -- <strong>Logistics Object</strong> | A resource in the BOL system that represents a business entity, such as a QuotationRequest, Quotation, or Service. Logistics Objects are managed through the BOL API and can be accessed or modified by Users based on their permissions i.e. based on their AD Entra ID roles and ACL (Access Control List) modes. <strong>Member</strong> | A data owner in the BOL system, uniquely identified by its `memberIdentifier`. Members are used to manage access control and ownership of Logistics Objects. <strong>User</strong> | A person or entity that interacts with the BOL system, typically through a client application, and beeing authenticated. Users are associated with Members and can perform actions on Logistics Objects based on their access level. User access token includes the Member identifier as part of roles claims. Each path in the API is protected by a custom role defined in AD Entra ID, and operations on Logistics Objects are protected by specific Access modes defined by members. <strong>Access Control</strong> | The mechanism by which the BOL system restricts access to API paths and resources based on the identity of the User and their associated Member. Access control is enforced through AD Entra ID roles and ACL (Access Control List) modes defined for every single Logistics Object. <strong>ACL (Access Control List)</strong> | A set of authorizations that define what actions a User can perform on a Logistics Object. The level of access granted to Users is determined by ACL modes defined in authorizations for specific Logistics Objects. ACL Modes are not directly associated with Users but rather with Members, which are the data owners of Logistics Objects. Each Logistics Object has an ACL that specifies the access modes for different Members. Members need to exist in the BOL system to be granted access to Logistics Objects. <strong>ACL Mode</strong> | The specific level of access control applied to a Logistics Object, determining what actions can be performed by Users. ACL modes are defined for each Logistics Object by `memberIdentifier` and include `READ`, `WRITE`, and `CONTROL`. Each mode includes access granted by the modes under it, so `CONTROL` includes `WRITE` and `READ`, and `WRITE` includes `READ`. <strong>Change Request</strong> | A request to modify a Logistics Object, which is processed by the BOL system. Change requests are created when a User wants to update an existing Logistics Object, and they are subject to approval and application by the system. All changes to Logistics Objects are done through change requests. This allows for a controlled and auditable process of modifications. <strong>Host</strong> | A server alias in the BOL system, used to abstract URI values internally. This allows for flexibility in the underlying infrastructure when migrating or changing server configurations. 
 *
 * The version of the OpenAPI document: 7.2.1-develop.2
 * Contact: benoit.tiers@logist-x.eu
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { ExecutionScheduledLocation } from './ExecutionScheduledLocation';
import {
    ExecutionScheduledLocationFromJSON,
    ExecutionScheduledLocationFromJSONTyped,
    ExecutionScheduledLocationToJSON,
    ExecutionScheduledLocationToJSONTyped,
} from './ExecutionScheduledLocation';
import type { SpecialInstruction } from './SpecialInstruction';
import {
    SpecialInstructionFromJSON,
    SpecialInstructionFromJSONTyped,
    SpecialInstructionToJSON,
    SpecialInstructionToJSONTyped,
} from './SpecialInstruction';
import type { SpecialCharacteristic } from './SpecialCharacteristic';
import {
    SpecialCharacteristicFromJSON,
    SpecialCharacteristicFromJSONTyped,
    SpecialCharacteristicToJSON,
    SpecialCharacteristicToJSONTyped,
} from './SpecialCharacteristic';
import type { Party } from './Party';
import {
    PartyFromJSON,
    PartyFromJSONTyped,
    PartyToJSON,
    PartyToJSONTyped,
} from './Party';
import type { ExternalReference } from './ExternalReference';
import {
    ExternalReferenceFromJSON,
    ExternalReferenceFromJSONTyped,
    ExternalReferenceToJSON,
    ExternalReferenceToJSONTyped,
} from './ExternalReference';
import type { Value } from './Value';
import {
    ValueFromJSON,
    ValueFromJSONTyped,
    ValueToJSON,
    ValueToJSONTyped,
} from './Value';
import type { ServiceLevelAgreement } from './ServiceLevelAgreement';
import {
    ServiceLevelAgreementFromJSON,
    ServiceLevelAgreementFromJSONTyped,
    ServiceLevelAgreementToJSON,
    ServiceLevelAgreementToJSONTyped,
} from './ServiceLevelAgreement';
import type { Operation } from './Operation';
import {
    OperationFromJSON,
    OperationFromJSONTyped,
    OperationToJSON,
    OperationToJSONTyped,
} from './Operation';
import type { Document } from './Document';
import {
    DocumentFromJSON,
    DocumentFromJSONTyped,
    DocumentToJSON,
    DocumentToJSONTyped,
} from './Document';
import type { Link } from './Link';
import {
    LinkFromJSON,
    LinkFromJSONTyped,
    LinkToJSON,
    LinkToJSONTyped,
} from './Link';
import type { Contact } from './Contact';
import {
    ContactFromJSON,
    ContactFromJSONTyped,
    ContactToJSON,
    ContactToJSONTyped,
} from './Contact';
import type { Location } from './Location';
import {
    LocationFromJSON,
    LocationFromJSONTyped,
    LocationToJSON,
    LocationToJSONTyped,
} from './Location';

/**
 * When a customer wants to delegate an activity to a provider, they submit a service request. The service request then follows an approval process to eventually result in a service being owned by the provider and linked to the delegated activity. In response to a service request, the provider proposes a service, which the customer can either accept or reject.
 *                 The customer may issue several service requests for an activity, sometimes because the activity is proposed to several providers, sometimes because there was a problem with a service request and a new one had to be sent. However, ultimately, only one service can be executed for a delegated activity, and all other service requests are discarded.
 * @export
 * @interface ServiceRequest
 */
export interface ServiceRequest {
    /**
     * Indicates whether the service has to be considered as Dangerous. It depends on the type of service and the regulations applied in the different countries.
     * Carrying dangerous goods doesn't mean a transport "is dangerous". Applying the dangerous goods policies determines whether the service is dangerous or not.
     * @type {boolean}
     * @memberof ServiceRequest
     */
    isDangerous?: boolean;
    /**
     * Free text to describe the product in case there is not detailed description by BatchOfGoods, piece or item.
     * @type {string}
     * @memberof ServiceRequest
     */
    productDescription?: string;
    /**
     * Date and time the service request was issued by the customer. UTC timestamp including milliseconds.
     * @type {Date}
     * @memberof ServiceRequest
     */
    requestDateTime?: Date;
    /**
     * Status of the service request from the request to the service proposed.
     *                              List of possible values:
     *                              - REQUESTED: service Request has been issued by the requestor
     *                              - RESPONDED: the provider has replied to the service request whatever the answer is
     *                              - CANCELLED: the requestor has cancelled his service request.
     * @type {string}
     * @memberof ServiceRequest
     */
    requestStatus?: string;
    /**
     * Date and time the provider has answered. UTC timestamp including milliseconds.
     * @type {Date}
     * @memberof ServiceRequest
     */
    responseDateTime?: Date;
    /**
     * 
     * @type {string}
     * @memberof ServiceRequest
     */
    responseStatus?: string;
    /**
     * ID of a service agreement or contract the service request refers to, for the provider to create a service accordingly.
     * @type {string}
     * @memberof ServiceRequest
     */
    serviceAgreementReference?: string;
    /**
     * Free text to add a service description to be reused by the provider.
     * @type {string}
     * @memberof ServiceRequest
     */
    serviceDescription?: string;
    /**
     * A short name of the service agreed upon between the requestor and the provider to facilitate searching.
     * @type {string}
     * @memberof ServiceRequest
     */
    serviceName?: string;
    /**
     * Refers to Service nature.
     * @type {string}
     * @memberof ServiceRequest
     */
    serviceNature?: string;
    /**
     * Refers to service subtype.
     * @type {string}
     * @memberof ServiceRequest
     */
    serviceSubtype?: string;
    /**
     * Refers to service Type.
     * @type {string}
     * @memberof ServiceRequest
     */
    serviceType?: string;
    /**
     * The Intercom (International Commercial Terms) is a 3 digits codes defining the the contractual terms between the seller and the buyer regarding the delivery of the goods and the payment of the taxes. The International Chamber of Commerce (ICC) provides the list of the codes. This incoterm is the one given buy the seller to the service provider to apply the transport rule.
     * @type {string}
     * @memberof ServiceRequest
     */
    incoterm?: string;
    /**
     * Consignments to be served by the service request.
     * @type {Array<Link>}
     * @memberof ServiceRequest
     */
    consignments?: Array<Link>;
    /**
     * Scheduled location where the service is requested to start.
     *                     It can be the same location as the location of startLocation or executionLocation of the first activity but not always.
     *                     Service Start location and first activity location would be different when an activity has to be performed before starting the execution of the service itself.
     *                     It contains also the requested, scheduled and actual calendar at the start location
     * @type {ExecutionScheduledLocation}
     * @memberof ServiceRequest
     */
    startLocation?: ExecutionScheduledLocation;
    /**
     * Scheduled location where the service is requested to end.
     *                     It can be the same location as the location of endLocation or executionLocation of the last activity but not always.
     *                     Service end location and last activity location would be different when an activity has to be performed after the service is rendered.
     *                     It contains also the requested, scheduled and actual calendar at the end location
     * @type {ExecutionScheduledLocation}
     * @memberof ServiceRequest
     */
    endLocation?: ExecutionScheduledLocation;
    /**
     * List the scheduled activities requested by the customer.
     * @type {Array<Link>}
     * @memberof ServiceRequest
     */
    requestedActivities?: Array<Link>;
    /**
     * List of contacts related to the serviceRequest. These can be individuals or teams that need to be contacted regarding the service once it is approved.
     * @type {Array<Contact>}
     * @memberof ServiceRequest
     */
    contacts?: Array<Contact>;
    /**
     * List of documents used for the service request.
     * @type {Array<Document>}
     * @memberof ServiceRequest
     */
    documents?: Array<Document>;
    /**
     * External references of the service request should be used when they are defined in a legacy system that registers the service request under a different reference identifier than the URI. This is useful for searching the service request using the ID from the legacy app. Frequent ExternalReference Types:
     *                                   - 'CUSTOMER_ID'
     *                                   - 'PROVIDER_ID'
     * @type {Array<ExternalReference>}
     * @memberof ServiceRequest
     */
    externalReferences?: Array<ExternalReference>;
    /**
     * The final delivery location of the goods. This information is useful when a transport service is not delivering the goods to the final location, in order to calculate the taxes.
     * @type {Location}
     * @memberof ServiceRequest
     */
    finalLocation?: Location;
    /**
     * The origin location the goods are shipped from. This information may be useful when a transport service is not starting at the origin of the logistic chain to calculate the taxes of the service.
     * @type {Location}
     * @memberof ServiceRequest
     */
    originLocation?: Location;
    /**
     * The parent activities are the activities delegated to the service. In the case of simple subcontracting, only one activity is subcontracted, meaning there is only one parent activity for a service. Many activities from different services can be subcontracted into one service. For example, this would happen for groupage or deliveries of goods put in the same milk run. In that case, the list of parent activities contains all the activities of the services subcontracted.
     * @type {Array<Link>}
     * @memberof ServiceRequest
     */
    parentActivities?: Array<Link>;
    /**
     * The provider is the party accountable for the execution of the service and is the owner of the service.
     * @type {Party}
     * @memberof ServiceRequest
     */
    provider?: Party;
    /**
     * The requestor refers to the party who initiates the service request. Typically, the requestor is the shipper, but in certain cases, such as when dealing with complex organizations, another party may be responsible for requesting the service.
     * @type {Party}
     * @memberof ServiceRequest
     */
    requestor?: Party;
    /**
     * The sales operations refer to the unit operations to be invoiced in the service. These operations can either be known upfront at the time of requesting the service or can be added later.
     * @type {Array<Operation>}
     * @memberof ServiceRequest
     */
    saleOperations?: Array<Operation>;
    /**
     * 
     * @type {Link}
     * @memberof ServiceRequest
     */
    service?: Link;
    /**
     * SLA stands for Service Level Agreement. It is an agreement between the customer and the provider that defines the level of service to be provided as part of their contractual relationship. Although there is no need to standardize this B2B value, it is important for both parties to clearly define the service levels and expectations to avoid any misunderstandings or disputes.
     * @type {ServiceLevelAgreement}
     * @memberof ServiceRequest
     */
    sla?: ServiceLevelAgreement;
    /**
     * Amount declared for insurance or customs declaration purpose.
     * @type {Value}
     * @memberof ServiceRequest
     */
    declaredValue?: Value;
    /**
     * Special characteristics applied to the service request and to be shared between the customer and the provider.
     * @type {Array<SpecialCharacteristic>}
     * @memberof ServiceRequest
     */
    specialCharacteristics?: Array<SpecialCharacteristic>;
    /**
     * Special instructions applied to the service request and to be shared with a provider, such as handling instructions or special protocol to apply.
     * @type {Array<SpecialInstruction>}
     * @memberof ServiceRequest
     */
    specialInstructions?: Array<SpecialInstruction>;
    /**
     * The invoiced party is the party invoiced for the service.
     * @type {Party}
     * @memberof ServiceRequest
     */
    invoicedParty?: Party;
}

/**
 * Check if a given object implements the ServiceRequest interface.
 */
export function instanceOfServiceRequest(value: object): value is ServiceRequest {
    return true;
}

export function ServiceRequestFromJSON(json: any): ServiceRequest {
    return ServiceRequestFromJSONTyped(json, false);
}

export function ServiceRequestFromJSONTyped(json: any, ignoreDiscriminator: boolean): ServiceRequest {
    if (json == null) {
        return json;
    }
    return {
        
        'isDangerous': json['isDangerous'] == null ? undefined : json['isDangerous'],
        'productDescription': json['productDescription'] == null ? undefined : json['productDescription'],
        'requestDateTime': json['requestDateTime'] == null ? undefined : (new Date(json['requestDateTime'])),
        'requestStatus': json['requestStatus'] == null ? undefined : json['requestStatus'],
        'responseDateTime': json['responseDateTime'] == null ? undefined : (new Date(json['responseDateTime'])),
        'responseStatus': json['responseStatus'] == null ? undefined : json['responseStatus'],
        'serviceAgreementReference': json['serviceAgreementReference'] == null ? undefined : json['serviceAgreementReference'],
        'serviceDescription': json['serviceDescription'] == null ? undefined : json['serviceDescription'],
        'serviceName': json['serviceName'] == null ? undefined : json['serviceName'],
        'serviceNature': json['serviceNature'] == null ? undefined : json['serviceNature'],
        'serviceSubtype': json['serviceSubtype'] == null ? undefined : json['serviceSubtype'],
        'serviceType': json['serviceType'] == null ? undefined : json['serviceType'],
        'incoterm': json['incoterm'] == null ? undefined : json['incoterm'],
        'consignments': json['consignments'] == null ? undefined : ((json['consignments'] as Array<any>).map(LinkFromJSON)),
        'startLocation': json['startLocation'] == null ? undefined : ExecutionScheduledLocationFromJSON(json['startLocation']),
        'endLocation': json['endLocation'] == null ? undefined : ExecutionScheduledLocationFromJSON(json['endLocation']),
        'requestedActivities': json['requestedActivities'] == null ? undefined : ((json['requestedActivities'] as Array<any>).map(LinkFromJSON)),
        'contacts': json['contacts'] == null ? undefined : ((json['contacts'] as Array<any>).map(ContactFromJSON)),
        'documents': json['documents'] == null ? undefined : ((json['documents'] as Array<any>).map(DocumentFromJSON)),
        'externalReferences': json['externalReferences'] == null ? undefined : ((json['externalReferences'] as Array<any>).map(ExternalReferenceFromJSON)),
        'finalLocation': json['finalLocation'] == null ? undefined : LocationFromJSON(json['finalLocation']),
        'originLocation': json['originLocation'] == null ? undefined : LocationFromJSON(json['originLocation']),
        'parentActivities': json['parentActivities'] == null ? undefined : ((json['parentActivities'] as Array<any>).map(LinkFromJSON)),
        'provider': json['provider'] == null ? undefined : PartyFromJSON(json['provider']),
        'requestor': json['requestor'] == null ? undefined : PartyFromJSON(json['requestor']),
        'saleOperations': json['saleOperations'] == null ? undefined : ((json['saleOperations'] as Array<any>).map(OperationFromJSON)),
        'service': json['service'] == null ? undefined : LinkFromJSON(json['service']),
        'sla': json['sla'] == null ? undefined : ServiceLevelAgreementFromJSON(json['sla']),
        'declaredValue': json['declaredValue'] == null ? undefined : ValueFromJSON(json['declaredValue']),
        'specialCharacteristics': json['specialCharacteristics'] == null ? undefined : ((json['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicFromJSON)),
        'specialInstructions': json['specialInstructions'] == null ? undefined : ((json['specialInstructions'] as Array<any>).map(SpecialInstructionFromJSON)),
        'invoicedParty': json['invoicedParty'] == null ? undefined : PartyFromJSON(json['invoicedParty']),
    };
}

export function ServiceRequestToJSON(json: any): ServiceRequest {
    return ServiceRequestToJSONTyped(json, false);
}

export function ServiceRequestToJSONTyped(value?: ServiceRequest | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'isDangerous': value['isDangerous'],
        'productDescription': value['productDescription'],
        'requestDateTime': value['requestDateTime'] == null ? value['requestDateTime'] : value['requestDateTime'].toISOString(),
        'requestStatus': value['requestStatus'],
        'responseDateTime': value['responseDateTime'] == null ? value['responseDateTime'] : value['responseDateTime'].toISOString(),
        'responseStatus': value['responseStatus'],
        'serviceAgreementReference': value['serviceAgreementReference'],
        'serviceDescription': value['serviceDescription'],
        'serviceName': value['serviceName'],
        'serviceNature': value['serviceNature'],
        'serviceSubtype': value['serviceSubtype'],
        'serviceType': value['serviceType'],
        'incoterm': value['incoterm'],
        'consignments': value['consignments'] == null ? undefined : ((value['consignments'] as Array<any>).map(LinkToJSON)),
        'startLocation': ExecutionScheduledLocationToJSON(value['startLocation']),
        'endLocation': ExecutionScheduledLocationToJSON(value['endLocation']),
        'requestedActivities': value['requestedActivities'] == null ? undefined : ((value['requestedActivities'] as Array<any>).map(LinkToJSON)),
        'contacts': value['contacts'] == null ? undefined : ((value['contacts'] as Array<any>).map(ContactToJSON)),
        'documents': value['documents'] == null ? undefined : ((value['documents'] as Array<any>).map(DocumentToJSON)),
        'externalReferences': value['externalReferences'] == null ? undefined : ((value['externalReferences'] as Array<any>).map(ExternalReferenceToJSON)),
        'finalLocation': LocationToJSON(value['finalLocation']),
        'originLocation': LocationToJSON(value['originLocation']),
        'parentActivities': value['parentActivities'] == null ? undefined : ((value['parentActivities'] as Array<any>).map(LinkToJSON)),
        'provider': PartyToJSON(value['provider']),
        'requestor': PartyToJSON(value['requestor']),
        'saleOperations': value['saleOperations'] == null ? undefined : ((value['saleOperations'] as Array<any>).map(OperationToJSON)),
        'service': LinkToJSON(value['service']),
        'sla': ServiceLevelAgreementToJSON(value['sla']),
        'declaredValue': ValueToJSON(value['declaredValue']),
        'specialCharacteristics': value['specialCharacteristics'] == null ? undefined : ((value['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicToJSON)),
        'specialInstructions': value['specialInstructions'] == null ? undefined : ((value['specialInstructions'] as Array<any>).map(SpecialInstructionToJSON)),
        'invoicedParty': PartyToJSON(value['invoicedParty']),
    };
}

